import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { logger } from '@vista/omnichannel-components-domain';
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { useMountEffect } from '@vista-digital/react-tools';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import { ZoomControllerButtons } from './subcomponents/zoom-controller-buttons';
import { keyDownHandler } from './utils/key-down-handler';
import { ZoomHandler } from './utils/zoom-handler';
const defaultZoomButtonPadding = 44;
const b = bemClassBuilder('v-zoom-controller');
const bViewport = bemClassBuilder('v-zoom-controller-viewport');
/**
 * Renders a ZoomController component that handles zooming and panning of a child element.
 * Supports mouse, keyboard and touch input.
 */
export const ZoomControllerBase = (props, ref) => {
    const handlerRef = useRef(new ZoomHandler());
    const viewportRef = useRef(null);
    const contentRef = useRef(null);
    const { showPanButtons, showZoomButtons, contentDimensions, className, onContentTransformationChanged } = props;
    const handleContentTransformationChanged = useCallback((transformation) => {
        const viewport = viewportRef.current;
        if (!viewport) {
            logger.error('Cannot handle content transformation with null viewport');
            return;
        }
        onContentTransformationChanged === null || onContentTransformationChanged === void 0 ? void 0 : onContentTransformationChanged(transformation);
        const zoomedInClass = 'v-zoom-controller-viewport--is-zoomed-in';
        if (handlerRef.current.isZoomedIn)
            viewport.classList.add(zoomedInClass);
        else
            viewport.classList.remove(zoomedInClass);
        const animatingClass = 'v-zoom-controller-viewport--is-animating';
        if (handlerRef.current.isAnimating)
            viewport.classList.add(animatingClass);
        else
            viewport.classList.remove(animatingClass);
    }, [onContentTransformationChanged]);
    const setUpHandler = useCallback(() => {
        const viewport = viewportRef.current;
        const content = contentRef.current;
        if (!viewport || !content) {
            logger.error('Cannot setup handler with null viewport or null content');
            return;
        }
        handlerRef.current.onContentTransformationChanged = handleContentTransformationChanged;
        handlerRef.current.setElements(viewport, content, contentDimensions);
        handlerRef.current.zoomButtonPadding = showZoomButtons ? defaultZoomButtonPadding : 0;
    }, [contentDimensions, showZoomButtons, handleContentTransformationChanged]);
    useMountEffect(() => {
        requestAnimationFrame(() => setUpHandler());
        return () => handlerRef.current.unmount();
    });
    useEffect(setUpHandler, [setUpHandler]);
    const handleOnKeyDown = event => keyDownHandler(handlerRef.current, event);
    useImperativeHandle(ref, () => ({
        resetZoom: () => handlerRef.current.resetZoom(),
        setUpHandler,
        handleKeyDown: handleOnKeyDown,
        handleContentTransformationChanged
    }));
    return (_jsxs("div", Object.assign({ className: b.block({ showPanButtons }, className), onKeyDown: handleOnKeyDown, tabIndex: 0 }, { children: [_jsx("div", Object.assign({ className: bViewport.block(), ref: viewportRef }, { children: _jsx("div", Object.assign({ className: bViewport.element('content'), ref: contentRef }, { children: props.children })) })), _jsx(ZoomControllerButtons, { handler: handlerRef.current, showPanButtons: showPanButtons, showZoomButtons: showZoomButtons })] })));
};
export const ZoomController = forwardRef(ZoomControllerBase);
ZoomController.displayName = 'ZoomController';
